// To process checkout data used by my-account & registration flows
import type { Session } from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import {
  DynamicObjectInterface,
  RoutePaths,
  AccountDetailRenewalStatusEnum,
  CheckoutPayloadType,
  AccountProcessType,
  FunctionType,
} from '@commerce-profile-webcomponents/sdk';
import {
  ApiProjections,
  Cart,
  cartApi,
  CartConstantsEnum,
  OrderType,
  updateCart,
} from '@commerce-cart-webcomponents/sdk';
import { createCheckout } from '@commerce-checkout-webcomponents/ui';
import { v5 as uuidv5 } from 'uuid';
import { CartUpdateType } from './enums/enums';

export const resolveStringValue = (value?: DynamicObjectInterface, value2?: DynamicObjectInterface): string =>
  value || value2 || '';

export const createCheckoutWithCart = async ({
  registrationId,
  processType,
  accountType,
  feeSKU,
  kitSKU,
  lateFeeSKU,
  sessionData,
  profileData,
  redirectUrl,
}: CheckoutPayloadType) => {
  const entries: DynamicObjectInterface[] = [];

  if (feeSKU) {
    // Fee SKU
    entries.push({
      entryId: 1,
      isPersistent: true,
      isFreeItem: false,
      product: {
        id: feeSKU,
      },
      quantity: 1,
    });
  }

  if (kitSKU) {
    // Selected SKU for kit
    entries.push({
      entryId: entries.length + 1,
      isPersistent: processType !== AccountProcessType.Registration,
      isFreeItem: false,
      product: {
        id: kitSKU,
      },
      quantity: 1,
    });
  }

  if (lateFeeSKU) {
    // Selected SKU for kit
    entries.push({
      entryId: entries.length + 1,
      isPersistent: true,
      isFreeItem: false,
      product: {
        id: lateFeeSKU,
      },
      quantity: 1,
    });
  }

  const accountProcessData: DynamicObjectInterface = {
    id: registrationId,
    type: processType,
    businessNature: accountType,
    redirectUrl: redirectUrl ?? RoutePaths.ACCOUNT_INFORMATION,
  };

  const cart = createCartFromProfile(accountProcessData, entries, sessionData, profileData);

  return await createCheckout(cart);
};

const createCartFromProfile = (
  accountProcessData: DynamicObjectInterface,
  entries: DynamicObjectInterface,
  sessionData?: Session,
  profileData?: DynamicObjectInterface,
): Cart => {
  // By using this method, we create cart by using profile data
  const cartId = uuidv5(`${Date.now()}`, uuidv5.URL);

  const defaultAmwayUser = {
    firstName: resolveStringValue(sessionData?.act?.givenName),
    lastName: resolveStringValue(sessionData?.act?.familyName),
    accountType: resolveStringValue(sessionData?.type, accountProcessData?.businessNature),
    status: resolveStringValue(profileData?.status) as DynamicObjectInterface,
    renewalFlag: resolveStringValue(sessionData?.renewalStatus) as AccountDetailRenewalStatusEnum,
    renewalLifeCycle: resolveStringValue(sessionData?.renewalLifecycle),
    amwayUserId: resolveStringValue(sessionData?.id),
    accountId: resolveStringValue(sessionData?.aboNum),
    ...(sessionData?.accountExpirationDate && { expirationDate: new Date(sessionData.accountExpirationDate) }),
  };

  return {
    id: cartId,
    orderType: OrderType.Regular,
    amwayUser: defaultAmwayUser,
    accountProcess: accountProcessData,
    subCarts: [
      {
        subCartId: cartId,
        entries: entries,
        amwayUser: {
          ...defaultAmwayUser,
          lastRenewedDate: new Date(profileData?.lastRenewedDate || Date.now()),
          entryDate: new Date(profileData?.entryDate || Date.now()),
          sponsor: sessionData?.uplineSponsorAccount,
          // currently we do not have: uplinePlatinum, currentAwardLevel, highestAwardLevel, loaCode
          // ignore for now
        },
        totalEntries: entries.length,
        totalAmPoint: 0,
        totalEarnedAmPoint: 0,
        totalAmPointDiscount: 0,
        isRationingApplied: false,
        errors: [],
        proxySubCart: false,
      },
    ],
    totalEntries: entries.length,
    version: 0,
    metaData: {
      createdAt: new Date(),
    },
  };
};

// Currently used by registration skip cart flow
export const appendToCart = async (
  dispatch: FunctionType,
  cartData: Cart,
  sku?: string,
  feeSku?: string,
  updateCartType = CartUpdateType.CART,
) => {
  if (!sku) {
    // without sku, use the regular updateCart
    dispatch(updateCart(cartData as Cart));
    return;
  }

  // This is only for adding entries to cart (new sku)
  const entries: DynamicObjectInterface[] = [];

  if (feeSku) {
    entries.push({
      product: {
        id: feeSku,
      },
      quantity: 1,
    });
  }
  if (sku) {
    entries.push({
      product: {
        id: sku,
      },
      quantity: 1,
    });
  }

  // Adding entries
  if (updateCartType === CartUpdateType.CART) {
    const payload = {
      subCarts: [
        {
          subCartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          entries,
        },
      ],
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };

    // call cart api to add entries
    const cartRes = await cartApi.addToCart({
      cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
      subCartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
      payload,
      projections: ApiProjections.DETAILED,
    });

    dispatch(updateCart(cartRes as Cart));
  } else {
    // Adding entries to memory cart
    const cartEntries = cartData?.subCarts?.find((subCart) => subCart?.subCartId === cartData?.id)?.entries || [];

    cartEntries.push(...entries);
    dispatch(updateCart(cartData as Cart));
  }
};
